import { useState, useEffect, useRef } from 'react';

/** 開發時可改為 console.log 以除錯 */
const devLog = (..._args: unknown[]) => {};

const CHECK_ENDPOINTS = ['/favicon.ico', '/manifest.json', '/'];

/**
 * 網路連線狀態檢測 Hook（參考 TaskyVenture 實現）
 * - 監聽 online/offline 事件
 * - 以 fetch 驗證實際連線（navigator.onLine 不可靠）
 * - 離線時每 2 秒嘗試重連
 * - 線上時每 15 秒定期檢測
 * - 支援頁面可見性變化時重新檢測
 */
export const useNetworkStatus = () => {
    const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
    const [wasOffline, setWasOffline] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const fastCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isPageVisible = useRef(true);
    const consecutiveFailures = useRef(0);
    const lastSuccessTime = useRef(Date.now());
    const recentFailures = useRef<number[]>([]);
    const isChecking = useRef(false);

    useEffect(() => {
        const handleOnline = () => {
            devLog('瀏覽器檢測到網路連線已恢復');
            setIsOnline(true);
            consecutiveFailures.current = 0;
            recentFailures.current = [];
            lastSuccessTime.current = Date.now();
            if (wasOffline) setWasOffline(false);
        };

        const handleOffline = () => {
            devLog('瀏覽器檢測到網路連線中斷');
            performQuickVerification();
        };

        const handleVisibilityChange = () => {
            const isVisible = !document.hidden;
            const wasVisible = isPageVisible.current;
            isPageVisible.current = isVisible;
            if (isVisible && !wasVisible) {
                devLog('頁面變為可見，檢查網路狀態');
                setTimeout(() => {
                    if (isPageVisible.current) performQuickVerification();
                }, 500);
            } else if (!isVisible && wasVisible) {
                stopFastCheck();
            }
        };

        const performQuickVerification = async () => {
            if (isChecking.current) return;
            isChecking.current = true;
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 3000);
                const response = await fetch('/favicon.ico', {
                    method: 'HEAD',
                    cache: 'no-cache',
                    signal: controller.signal,
                    headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
                });
                clearTimeout(timeoutId);
                if (response.ok) {
                    devLog('快速驗證：網路正常');
                    consecutiveFailures.current = 0;
                    recentFailures.current = [];
                    lastSuccessTime.current = Date.now();
                    if (!isOnline) {
                        setIsOnline(true);
                        setWasOffline(false);
                    }
                } else {
                    throw new Error('快速驗證失敗');
                }
            } catch {
                devLog('快速驗證失敗，判定為離線');
                setIsOnline(false);
                setWasOffline(true);
            } finally {
                isChecking.current = false;
            }
        };

        const checkConnection = async (isUrgent = false) => {
            if (!isPageVisible.current || isChecking.current) return;
            isChecking.current = true;
            const checkStartTime = Date.now();

            try {
                const timeoutDuration = isUrgent ? 2000 : 3000;
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

                let response: Response | undefined;
                let lastError: unknown;
                const promises = CHECK_ENDPOINTS.map((endpoint) =>
                    fetch(endpoint, {
                        method: 'HEAD',
                        cache: 'no-cache',
                        signal: controller.signal,
                        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
                    })
                );

                try {
                    response = await Promise.any(promises);
                } catch (aggregateError) {
                    lastError = aggregateError;
                }

                clearTimeout(timeoutId);
                const currentlyOnline = response?.ok;

                if (currentlyOnline) {
                    consecutiveFailures.current = 0;
                    recentFailures.current = [];
                    lastSuccessTime.current = Date.now();
                    if (!isOnline) {
                        devLog('網路狀態恢復: 離線 -> 線上');
                        setIsOnline(true);
                        setWasOffline(false);
                    }
                } else {
                    throw lastError ?? new Error('所有檢測端點都失敗');
                }
            } catch {
                const now = Date.now();
                consecutiveFailures.current++;
                recentFailures.current.push(now);
                recentFailures.current = recentFailures.current.filter((t) => now - t <= 10000);
                const timeSinceLastSuccess = now - lastSuccessTime.current;
                const checkDuration = now - checkStartTime;

                const shouldMarkOffline =
                    isPageVisible.current &&
                    isOnline &&
                    (!navigator.onLine ||
                        recentFailures.current.length >= 2 ||
                        timeSinceLastSuccess > 10000 ||
                        (consecutiveFailures.current >= 1 && checkDuration > 4000));

                if (shouldMarkOffline) {
                    devLog('判定為網路離線');
                    setIsOnline(false);
                    setWasOffline(true);
                    startFastReconnectCheck();
                } else if (consecutiveFailures.current >= 1) {
                    setTimeout(() => {
                        if (isPageVisible.current && isOnline) checkConnection(true);
                    }, 1000);
                }
            } finally {
                isChecking.current = false;
            }
        };

        const startFastReconnectCheck = () => {
            if (fastCheckRef.current) clearInterval(fastCheckRef.current);
            fastCheckRef.current = setInterval(() => {
                if (isPageVisible.current) checkConnection(true);
            }, 2000);
        };

        const stopFastCheck = () => {
            if (fastCheckRef.current) {
                clearInterval(fastCheckRef.current);
                fastCheckRef.current = null;
            }
        };

        const startPeriodicCheck = () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => {
                if (isPageVisible.current && isOnline) checkConnection();
            }, 15000);
        };

        const stopPeriodicCheck = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        isPageVisible.current = !document.hidden;

        if (isPageVisible.current) {
            setTimeout(() => checkConnection(), 100);
            startPeriodicCheck();
        }

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            stopPeriodicCheck();
            stopFastCheck();
        };
    }, [isOnline, wasOffline]);

    return {
        isOnline,
        wasOffline,
        recheckConnection: async () => {
            if (isChecking.current) return false;
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000);
                const promises = CHECK_ENDPOINTS.map((endpoint) =>
                    fetch(endpoint, {
                        method: 'HEAD',
                        cache: 'no-cache',
                        signal: controller.signal,
                        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
                    })
                );
                const response = await Promise.any(promises);
                clearTimeout(timeoutId);
                const currentlyOnline = response?.ok;
                if (currentlyOnline) {
                    setIsOnline(true);
                    setWasOffline(false);
                    return true;
                }
            } catch {
                // ignore
            }
            setIsOnline(false);
            setWasOffline(true);
            return false;
        },
    };
};
